/**
 * Settings: the health line (plan docs/plans/lms-plan.md 4.10, section 13 tab
 * 7 Settings, 6.6, G15; WP-48). GET /api/lms/health read-only, as the Settings
 * sub-view is the place 4.10 names for it:
 *
 * - Email notifications: on or off. It is the LMS_NOTIFICATIONS_ENABLED
 *   variable on the api service (G15, Appendix B), so this is an indicator,
 *   never a switch; while it is off every notice is still written to the
 *   in-app inbox with email suppressed.
 * - File storage: whether the bucket variable is set (certificates and
 *   uploads fail closed without it, 2.3a).
 * - Built-in catalog: the count of built-in course files waiting to be
 *   installed (6.6), with the Install button (POST /admin/catalog/install,
 *   WP-44's api/catalog.ts installCatalog; confirm first; nothing is ever
 *   published by an install, G8) and a link to the Catalog, where the drafts
 *   are read and published.
 * - The last daily pass and weekly digest (9.1, 9.3) and the emails that
 *   failed in the last 24 hours.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import ConfirmModal from '../../../../ui/ConfirmModal';
import { iconBtnCls, primaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { apiErrorMessage } from '../../../api/client';
import type { LmsHealth } from '../../../api/client';
import { installCatalog } from '../../../api/catalog';
import type { CatalogInstallResult } from '../../../api/catalog';
import { cardCls, ErrorNote, linkBtnCls, metaCls } from '../../../components/pickers/shared';
import { cardTitleCls, formatInstant, JobStatusPill, Pill } from './shared';

export interface StatusPanelProps {
  health: LmsHealth | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

/** The install toast (WP-44's wording: nothing was published, G8). */
export function installSummary(result: CatalogInstallResult): string {
  const installed = Array.isArray(result?.installed) ? result.installed.length : 0;
  const parts = [`${installed} installed as draft${installed === 1 ? '' : 's'}`];
  const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
  if (skipped > 0) parts.push(`${skipped} skipped`);
  const blocked = Array.isArray(result?.blocked) ? result.blocked.length : 0;
  if (blocked > 0) parts.push(`${blocked} blocked by an edited draft`);
  if (result?.profiles) parts.push(`${result.profiles.created} profile${result.profiles.created === 1 ? '' : 's'} created`);
  return `Catalog install: ${parts.join(', ')}. Nothing was published: read each draft in the Catalog and publish it.`;
}

function Row({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 py-2.5" data-testid={testId}>
      <dt className="text-sm text-gray-700 dark:text-gray-300">{label}</dt>
      <dd className="flex min-w-0 flex-col items-end gap-1 text-right text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function JobLine({ job, testId }: { job: { last_at: string | null; status: string | null } | null; testId: string }) {
  const when = formatInstant(job?.last_at ?? null);
  return (
    <>
      <JobStatusPill status={job?.status ?? null} testId={testId} />
      {when ? <span className={metaCls}>{`Last run ${when}`}</span> : null}
    </>
  );
}

export function StatusPanel({ health, loading, error, onRefresh }: StatusPanelProps) {
  const { toast } = useToast();
  const [confirmInstall, setConfirmInstall] = useState(false);
  const [installing, setInstalling] = useState(false);

  const install = async () => {
    setInstalling(true);
    try {
      const result = await installCatalog();
      toast(installSummary(result));
      onRefresh();
    } catch (err) {
      toast(apiErrorMessage(err, 'The catalog could not be installed.'), { type: 'error', duration: 0 });
    } finally {
      setInstalling(false);
      setConfirmInstall(false);
    }
  };

  const pending = health?.catalog_pending ?? null;
  const failed = health?.email_failed_24h ?? null;

  return (
    <section className={cardCls} aria-labelledby="lms-settings-status-title" data-testid="lms-settings-status">
      <div className="flex items-center justify-between gap-2">
        <h2 id="lms-settings-status-title" className={cardTitleCls}>
          Status
        </h2>
        <button
          type="button"
          className={iconBtnCls}
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh the status"
          data-testid="lms-settings-status-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {error ? (
        <div className="mt-3 space-y-2">
          <ErrorNote testId="lms-settings-status-error">{error}</ErrorNote>
        </div>
      ) : !health ? (
        <div className="mt-3 space-y-2" aria-busy="true">
          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ) : (
        <dl className="mt-1 divide-y divide-gray-100 dark:divide-gray-700">
          <Row label="Email notifications" testId="lms-status-email">
            {health.notifications_enabled ? (
              <Pill tone="green" testId="lms-status-email-pill">
                On
              </Pill>
            ) : (
              <Pill tone="gray" testId="lms-status-email-pill">
                Off
              </Pill>
            )}
            <span className={metaCls}>
              {health.notifications_enabled
                ? "Notices are emailed as well as shown in the inbox. Set on the server (LMS_NOTIFICATIONS_ENABLED)."
                : "Set on the server (LMS_NOTIFICATIONS_ENABLED), not here. While it is off, notices stay in the in-app inbox and no email is sent."}
            </span>
          </Row>

          <Row label="File storage" testId="lms-status-storage">
            {health.storage_configured ? (
              <Pill tone="green">Configured</Pill>
            ) : (
              <>
                <Pill tone="amber">Not configured</Pill>
                <span className={metaCls}>Certificates and uploads need GCS_LMS_BUCKET on the server.</span>
              </>
            )}
          </Row>

          <Row label="Built-in catalog" testId="lms-status-catalog">
            {pending === null ? (
              <Pill tone="gray">Unknown</Pill>
            ) : pending === 0 ? (
              <Pill tone="green">Up to date</Pill>
            ) : (
              <>
                <Pill tone="amber" testId="lms-status-catalog-pending">{`${pending.toLocaleString()} to install`}</Pill>
                <button
                  type="button"
                  className={primaryBtnCls}
                  onClick={() => setConfirmInstall(true)}
                  disabled={installing}
                  data-testid="lms-settings-install"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  {`Install (${pending.toLocaleString()})`}
                </button>
              </>
            )}
            <Link to="/lms/manage/catalog" className={linkBtnCls}>
              Open the catalog
            </Link>
          </Row>

          <Row label="Daily pass" testId="lms-status-daily">
            <JobLine job={health.jobs?.daily ?? null} testId="lms-status-daily-pill" />
          </Row>

          <Row label="Weekly digest" testId="lms-status-weekly">
            <JobLine job={health.jobs?.weekly_digest ?? null} testId="lms-status-weekly-pill" />
          </Row>

          <Row label="Failed emails, last 24 hours" testId="lms-status-failed">
            {failed === null ? (
              <Pill tone="gray">Unknown</Pill>
            ) : failed === 0 ? (
              <Pill tone="green">None</Pill>
            ) : (
              <Pill tone="red">{`${failed.toLocaleString()} failed`}</Pill>
            )}
          </Row>
        </dl>
      )}

      <ConfirmModal
        isOpen={confirmInstall}
        onClose={() => setConfirmInstall(false)}
        onConfirm={() => void install()}
        title="Install the built-in catalog?"
        message="New and changed built-in courses are added as drafts. Nothing is published: each draft waits in the Catalog until a person reads and publishes it."
        confirmLabel="Install"
        loading={installing}
      />
    </section>
  );
}

export default StatusPanel;
